import { useCallback } from "react";
import { useSendOTPMutation } from "../store/auth/otp.api";
import { useRecaptcha } from "./useRecaptcha";

/**
 * Custom hook that wraps useSendOTPMutation with invisible reCAPTCHA verification
 * Provides the same interface as the original RTK Query mutation hook
 * Includes retry functionality for reCAPTCHA failures
 *
 * @returns {Array} [mutationFunction, mutationState] - Same format as RTK Query mutations
 */
export const useSendOTPWithRecaptcha = () => {
  const [originalSendOTP, mutationState] = useSendOTPMutation();
  const { executeRecaptcha, resetRecaptcha } = useRecaptcha();

  const sendOTPWithRecaptcha = useCallback(
    async (data, retryCount = 0) => {
      const maxRetries = 2;

      try {
        const recaptchaToken = await executeRecaptcha();

        const requestData = {
          ...data,
          captcha_token: recaptchaToken
        };

        const result = await originalSendOTP(requestData).unwrap();

        resetRecaptcha();

        return { data: result };
      } catch (error) {
        resetRecaptcha();

        const isRecaptchaError = error.message && error.message.includes("reCAPTCHA");
        const isTokenError =
          error.data?.message?.toLowerCase().includes("captcha") ||
          error.data?.detail?.toLowerCase().includes("captcha");

        if ((isRecaptchaError || isTokenError) && retryCount < maxRetries) {
          console.log(`reCAPTCHA attempt ${retryCount + 1} failed, retrying...`);
          // Wait a brief moment before retry
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return sendOTPWithRecaptcha(data, retryCount + 1);
        }

        // If it's a reCAPTCHA error and we've exhausted retries
        if (isRecaptchaError || isTokenError) {
          throw {
            error: {
              data: {
                message: "Security verification failed. Please try again.",
                detail: "reCAPTCHA verification failed after retries"
              }
            }
          };
        }

        throw { error };
      }
    },
    [originalSendOTP, executeRecaptcha, resetRecaptcha]
  );

  return [sendOTPWithRecaptcha, mutationState];
};

/**
 * Backward compatible export - this allows existing components to use the reCAPTCHA version
 * by simply changing their import statement, without changing any other code
 */
export { useSendOTPWithRecaptcha as useSendOTPMutationWithRecaptcha };
